import React from "react";
import { Circles } from "react-loader-spinner";
import ImageIcon from "@mui/icons-material/Image";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonIcon from "@mui/icons-material/Person";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { formatBytes, MetricTile, PanelCard, SectionHeader } from "../components/accountUi";

export type StorageImage = {
  url: string | null;
  sizeBytes: number;
  type: string;
  label: string;
};

export type StorageSummaryRow = {
  id: string;
  label: string;
  sizeBytes: number;
  objectCount: number;
  percentOfTotal: number;
};

export type StorageUsage = {
  sizeBytes: number;
  objectCount: number;
  scannedAt?: string;
  summary?: StorageSummaryRow[];
  bookingSite?: {
    sizeBytes: number;
    objectCount: number;
    status?: string | null;
    publicUrl?: string;
    images: StorageImage[];
  } | null;
};

const IMAGE_TYPE_LABELS: Record<string, string> = {
  gallery: "Gallery",
  "before-after": "Before / after",
  dentist: "Dentist photo",
  booking: "Booking",
};

const STORAGE_CATEGORY_ICONS: Record<string, React.ElementType> = {
  branding: PaletteOutlinedIcon,
  staff: GroupsOutlinedIcon,
  patients: PersonIcon,
  treatments: LocalHospitalOutlinedIcon,
  booking: BookOnlineIcon,
  other: FolderOutlinedIcon,
};

type StorageTabPanelProps = {
  storage: StorageUsage | null;
  storageLoading: boolean;
  storageError: string | null;
  onRefresh: () => void;
};

export default function StorageTabPanel({
  storage,
  storageLoading,
  storageError,
  onRefresh,
}: StorageTabPanelProps) {
  const summaryRows = storage?.summary ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-800 dark:text-white">
            File storage overview
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Space used by your clinic across all categories
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={storageLoading}
          className="inline-flex items-center justify-center gap-2 self-start rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 sm:self-auto"
        >
          <RefreshIcon
            sx={{ fontSize: 16 }}
            className={storageLoading ? "animate-spin text-primary" : ""}
          />
          Refresh scan
        </button>
      </div>

      {storageLoading && !storage ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <Circles height="44" width="44" color="#174d7a" ariaLabel="loading storage" />
          <p className="text-sm text-slate-500">Analyzing storage usage…</p>
        </div>
      ) : storageError ? (
        <div className="rounded-xl border border-amber-200/80 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-200">
          {storageError}
        </div>
      ) : storage ? (
        <div className="xl:grid xl:grid-cols-12 xl:gap-4 xl:items-start">
          <div className="space-y-4 xl:col-span-5 2xl:col-span-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 xl:grid-cols-1 2xl:grid-cols-3">
            <MetricTile label="Total used" value={formatBytes(storage.sizeBytes)} accent />
            <MetricTile
              label="Total files"
              value={storage.objectCount.toLocaleString()}
            />
            <MetricTile
              label="Last scanned"
              value={
                storage.scannedAt
                  ? new Date(storage.scannedAt).toLocaleString(undefined, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })
                  : "—"
              }
            />
          </div>

          {summaryRows.length > 0 ? (
            <PanelCard className="overflow-hidden">
              <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-700/80">
                <SectionHeader
                  icon={StorageOutlinedIcon}
                  title="Usage by category"
                  subtitle="Breakdown of stored files"
                />
              </div>
              <div className="divide-y divide-slate-100 dark:divide-slate-700/80">
                {summaryRows.map((row) => {
                  const CatIcon = STORAGE_CATEGORY_ICONS[row.id] || FolderOutlinedIcon;
                  return (
                    <div key={row.id} className="px-5 py-4">
                      <div className="mb-3 flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600 dark:bg-slate-700/60 dark:text-slate-300">
                          <CatIcon sx={{ fontSize: 18 }} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-baseline justify-between gap-2">
                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                              {row.label}
                            </span>
                            <span className="text-sm font-bold text-primary">
                              {formatBytes(row.sizeBytes)}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            {row.objectCount} file{row.objectCount === 1 ? "" : "s"} ·{" "}
                            {row.percentOfTotal}% of total
                          </p>
                        </div>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-primary to-primaryLight transition-all duration-500"
                          style={{
                            width: `${Math.min(100, Math.max(2, row.percentOfTotal))}%`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </PanelCard>
          ) : null}
          </div>

          <div className="mt-4 space-y-4 xl:col-span-7 xl:mt-0 2xl:col-span-8">
          {storage.bookingSite && (
            <PanelCard className="overflow-hidden h-full">
              <div className="border-b border-slate-100 bg-gradient-to-r from-primary/[0.06] to-transparent px-5 py-4 dark:border-slate-700/80 dark:from-primary/10">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm dark:bg-slate-800">
                      <BookOnlineIcon sx={{ fontSize: 22 }} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                        Booking page images
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        Gallery & before/after photos on your public booking page
                      </p>
                      {storage.bookingSite.status ? (
                        <span
                          className={`mt-2 inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                            String(storage.bookingSite.status).toLowerCase() === "published"
                              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                              : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                          }`}
                        >
                          {storage.bookingSite.status}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <span className="rounded-lg bg-white px-2.5 py-1 font-bold text-primary shadow-sm dark:bg-slate-800">
                      {formatBytes(storage.bookingSite.sizeBytes)}
                    </span>
                    <span className="text-slate-500 dark:text-slate-400">
                      {storage.bookingSite.objectCount} file
                      {storage.bookingSite.objectCount === 1 ? "" : "s"}
                    </span>
                    {storage.bookingSite.publicUrl ? (
                      <a
                        href={storage.bookingSite.publicUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:opacity-95"
                      >
                        View booking page
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </a>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="p-5">
                {storage.bookingSite.images.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-3 2xl:grid-cols-4">
                    {storage.bookingSite.images.map((img, index) => (
                      <div
                        key={`${img.type}-${index}`}
                        className="group overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm transition hover:shadow-md dark:border-slate-600/80 dark:bg-slate-800"
                      >
                        <div className="relative aspect-[4/3] overflow-hidden bg-slate-100 dark:bg-slate-900">
                          {img.url ? (
                            <img
                              src={img.url}
                              alt={IMAGE_TYPE_LABELS[img.type] || img.label}
                              className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center">
                              <ImageIcon
                                className="text-slate-300 dark:text-slate-600"
                                sx={{ fontSize: 32 }}
                              />
                            </div>
                          )}
                        </div>
                        <div className="border-t border-slate-100 px-2.5 py-2 dark:border-slate-700">
                          <p className="truncate text-[10px] font-bold uppercase tracking-wide text-primary">
                            {IMAGE_TYPE_LABELS[img.type] || img.label}
                          </p>
                          <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                            {formatBytes(img.sizeBytes)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 py-12 text-center dark:border-slate-600">
                    <ImageIcon className="text-slate-300 dark:text-slate-600" sx={{ fontSize: 40 }} />
                    <p className="mt-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                      No booking page images uploaded yet
                    </p>
                  </div>
                )}
              </div>
            </PanelCard>
          )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
